import { useCallback } from 'react'

import { ChatParticipant } from '@tribeplatform/gql-client/types'
import { usePromoteChatAdmins } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { T, useI18n } from '../../i18n/index.js'

import { ModalMemberDetails } from './ModalMemberDetails.js'

interface Props {
  open: boolean
  participant: ChatParticipant
  onClose: () => void
}

export const PromoteModal = ({ open, onClose, participant }: Props) => {
  const { mutateAsync: promoteChatAdmins, isLoading } = usePromoteChatAdmins()
  const { $t } = useI18n()

  const onSubmit = useCallback(async () => {
    try {
      await promoteChatAdmins({
        chatId: participant.chatId,
        input: {
          memberIds: [participant.memberId],
        },
      })
      toast({
        title: $t({
          id: 'Generics.Success',
          defaultMessage: 'Success',
        }),
        description: $t({
          id: 'Messaging.Group.Promote.Success',
          defaultMessage: 'Promoted to group admin',
        }),
        status: 'success',
      })
      onClose()
    } catch (error) {
      toast({
        title: $t({
          id: 'Generics.Error',
          defaultMessage: 'Error',
        }),
        description: $t({
          id: 'Messaging.Group.Promote.Error',
          defaultMessage: 'Failed to promote to group admin',
        }),
        status: 'error',
      })
    }
  }, [$t, onClose, participant.chatId, participant.memberId, promoteChatAdmins])

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header
        title={
          <div className="flex items-center gap-2">
            <T
              defaultMessage="Promote {member_name} to group admin?"
              id="Messaging.PromoteModal.Title"
              values={{
                member_name: participant.name,
              }}
            />
          </div>
        }
        description={
          <T
            defaultMessage="{member_name} will have admin privileges and control over the group, which means they will be able to add or remove members, change the group name, and more."
            id="Messaging.PromoteModal.Description"
            values={{
              member_name: (
                <span className="font-bold">{participant.name}</span>
              ),
            }}
          />
        }
      />
      <Modal.Content>
        <ModalMemberDetails participant={participant} />
        <Modal.Actions className="!p-0">
          <Button variant="primary" onClick={onSubmit} loading={isLoading}>
            <T defaultMessage="Promote" id="Generics.Promote" />
          </Button>
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  )
}
